import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';

import { Period } from 'dashboard/lib/unixTime';
import {
  AnalyticsQueryTemplate,
  computeOffsetForQueryTemplateLoad,
  VerificationChecksAnalyticsQueryParams,
  VerificationVolumeAnalyticsQueryParams,
} from 'dashboard/models/analytics-query-template';
import { CheckCategory } from 'dashboard/models/check';
import { CheckPassType } from 'dashboard/models/inquiry';
import {
  MetricsVerificationChecksOverTime,
  MetricsVerificationChecksOverTimeMetaData,
} from 'dashboard/models/metrics';
import {
  FilterOptionKey,
  MetricsGroupBy,
  VerificationMetricsFilteredVolumeResponse,
  VerificationMetricsFilteredVolumeSummary,
  VerificationTemplatesFilterOptions,
  VerificationVolumeIntervals,
} from 'dashboard/models/metrics-verification';
import { FilterRule } from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/BaseVerificationFilterRow';
import {
  FilterDimensions,
  FilteredVerificationType,
} from 'dashboard/views/DashboardVerificationAnalyticsVolumeView/VerificationFilteredMetrics';
import { ApiModel, VerificationType } from 'lib/interfaces/api';
import { Iso8601DateString } from 'lib/types';

const NOW = DateTime.local();
const DEFAULT_CREATED_AT_START = NOW.minus({ days: 14 }).startOf('day');
const DEFAULT_CREATED_AT_END = NOW.endOf('day');

export interface DynamicFilterOptions {
  // These are going to be state/province/territory codes
  // We will need to dynamically read these since they're scanner extractions
  [FilterOptionKey.AamvaIssuingAuthorities]: string[];
  [FilterOptionKey.AamvaDeviceGroups]: string[];
  [FilterOptionKey.AamvaPlatforms]: string[];
  [FilterOptionKey.DatabaseCountryCodes]: string[];
  [FilterOptionKey.DatabaseDeviceGroups]: string[];
  [FilterOptionKey.DatabasePlatforms]: string[];
  [FilterOptionKey.GovernmentIdCaptureMethods]: string[];
  [FilterOptionKey.GovernmentIdDeviceGroups]: string[];
  [FilterOptionKey.GovernmentIdIssuingCountries]: string[];
  [FilterOptionKey.GovernmentIdIssuingSubdivisions]: string[];
  [FilterOptionKey.GovernmentIdSelectedCountries]: string[];
  [FilterOptionKey.GovernmentIdTypes]: string[];
  [FilterOptionKey.SelfieCaptureMethods]: string[];
  [FilterOptionKey.SelfieDeviceGroups]: string[];
  [FilterOptionKey.SelfiePlatforms]: string[];
  [FilterOptionKey.VerificationTemplates]: VerificationTemplatesFilterOptions;
}

export interface VerificationMetricsState {
  filteredVolumeSummary: VerificationMetricsFilteredVolumeSummary;
  verificationVolumeOverTime: VerificationVolumeIntervals;
  // New Checks
  verificationChecksOverTime: MetricsVerificationChecksOverTime;
  queriedAt: {
    volume: Iso8601DateString | undefined;
    funnel: Iso8601DateString | undefined;
    filteredVolume: Iso8601DateString | undefined;
    checks: Iso8601DateString | undefined;
  };
  updatedAt: {
    checks: Iso8601DateString | undefined;
  };
  // query filters that were the last filters sent to the server.
  lastQueryFilters?: VerificationMetricsFilterParams;
  filters: VerificationMetricsFilterParams;
  // Do not include these in the filters since we do not want to include it into the URL
  // TODO: [jonny] Examine useTable to see how we can fit this properly into a request + Type below better
  filterRules: Record<VerificationType, FilterRulesForType>;
  filterOptions: DynamicFilterOptions;
  volumeLastQueryFilterRules: Record<VerificationType, FilterRulesForType>;
  checksLastQueryFilterRules: Record<VerificationType, any>;
  verificationGranularities: VerificationGranularitiesByType;
  lastVerificationGranularities: {
    volume: VerificationGranularitiesByType;
    checks: VerificationGranularitiesByType;
  };
  selectedVolumeQueryTemplate: {
    template: AnalyticsQueryTemplate | undefined;
    computedOffsetStart: Iso8601DateString | undefined;
    computedOffsetEnd: Iso8601DateString | undefined;
  };
  selectedChecksQueryTemplate: {
    template: AnalyticsQueryTemplate | undefined;
    computedOffsetStart: Iso8601DateString | undefined;
    computedOffsetEnd: Iso8601DateString | undefined;
  };
}

export type VerificationTemplatesGroupedByType = Partial<
  Record<
    VerificationType,
    Array<{
      name: string;
      id: string;
      tooltipText?: string;
    }>
  >
>;

export type VerificationGranularitiesByType = Record<VerificationType, Period>;

export type FilterRulesForType = {
  or: Array<Array<FilterRule>>;
  verificationTemplateIds: string[];
  includeLegacyVerifications: boolean;
};
export const getInitialFilterRule: () => FilterRulesForType = () => ({
  or: [[{}]],
  verificationTemplateIds: [],
  includeLegacyVerifications: true,
});

export const initialState: VerificationMetricsState = {
  filteredVolumeSummary: {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    data: undefined,
  },
  verificationChecksOverTime: {
    data: [],
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  queriedAt: {
    volume: undefined,
    filteredVolume: undefined,
    funnel: undefined,
    checks: undefined,
  },
  updatedAt: {
    checks: undefined,
  },
  filters: {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    createdAtStart: undefined,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    createdAtEnd: undefined,
    templateIds: [],
    verificationTypes: [],
    txnTypeIds: [],
    groupBy: MetricsGroupBy.Inquiry,
    checkRequirements: [],
    checkTypes: [],
    groupByDimension: {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [ApiModel.VerificationDatabase]: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [ApiModel.VerificationGovernmentId]: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [ApiModel.VerificationSelfie]: null,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      [ApiModel.VerificationAamva]: null,
    },
  },
  lastQueryFilters: undefined,
  verificationVolumeOverTime: {
    data: {},
    domain: [DEFAULT_CREATED_AT_START.toUnixInteger(), DEFAULT_CREATED_AT_END.toUnixInteger()],
    period: Period.Day,
  },
  filterRules: {
    [ApiModel.VerificationAamva]: getInitialFilterRule(),
    [ApiModel.VerificationCertificateKorea]: getInitialFilterRule(),
    [ApiModel.VerificationDatabase]: getInitialFilterRule(),
    [ApiModel.VerificationDatabaseBusiness]: getInitialFilterRule(),
    [ApiModel.VerificationDatabaseBusinessFootprint]: getInitialFilterRule(),
    [ApiModel.VerificationDatabaseEcbsv]: getInitialFilterRule(),
    [ApiModel.VerificationDatabaseStandard]: getInitialFilterRule(),
    [ApiModel.VerificationDatabasePhoneCarrier]: getInitialFilterRule(),
    [ApiModel.VerificationDatabaseSerpro]: getInitialFilterRule(),
    [ApiModel.VerificationDatabaseTin]: getInitialFilterRule(),
    [ApiModel.VerificationDigitalIdUkSharecode]: getInitialFilterRule(),
    [ApiModel.VerificationDocument]: getInitialFilterRule(),
    [ApiModel.VerificationEmailAddress]: getInitialFilterRule(),
    [ApiModel.VerificationGovernmentId]: getInitialFilterRule(),
    [ApiModel.VerificationGovernmentIdNfc]: getInitialFilterRule(),
    [ApiModel.VerificationMobileDriversLicense]: getInitialFilterRule(),
    [ApiModel.VerificationPhoneNumber]: getInitialFilterRule(),
    [ApiModel.VerificationSelfie]: getInitialFilterRule(),
  },
  filterOptions: {
    [FilterOptionKey.AamvaIssuingAuthorities]: [],
    [FilterOptionKey.AamvaDeviceGroups]: [],
    [FilterOptionKey.AamvaPlatforms]: [],
    [FilterOptionKey.DatabaseCountryCodes]: [],
    [FilterOptionKey.DatabaseDeviceGroups]: [],
    [FilterOptionKey.DatabasePlatforms]: [],
    [FilterOptionKey.GovernmentIdCaptureMethods]: [],
    [FilterOptionKey.GovernmentIdDeviceGroups]: [],
    [FilterOptionKey.GovernmentIdIssuingCountries]: [],
    [FilterOptionKey.GovernmentIdIssuingSubdivisions]: [],
    [FilterOptionKey.GovernmentIdSelectedCountries]: [],
    [FilterOptionKey.GovernmentIdTypes]: [],
    [FilterOptionKey.SelfieCaptureMethods]: [],
    [FilterOptionKey.SelfieDeviceGroups]: [],
    [FilterOptionKey.SelfiePlatforms]: [],
    [FilterOptionKey.VerificationTemplates]: {},
  },
  verificationGranularities: {
    [ApiModel.VerificationAamva]: Period.Day,
    [ApiModel.VerificationCertificateKorea]: Period.Day,
    [ApiModel.VerificationDatabase]: Period.Day,
    [ApiModel.VerificationDatabaseBusiness]: Period.Day,
    [ApiModel.VerificationDatabaseBusinessFootprint]: Period.Day,
    [ApiModel.VerificationDatabaseStandard]: Period.Day,
    [ApiModel.VerificationDatabaseEcbsv]: Period.Day,
    [ApiModel.VerificationDatabasePhoneCarrier]: Period.Day,
    [ApiModel.VerificationDatabaseSerpro]: Period.Day,
    [ApiModel.VerificationDatabaseTin]: Period.Day,
    [ApiModel.VerificationDigitalIdUkSharecode]: Period.Day,
    [ApiModel.VerificationDocument]: Period.Day,
    [ApiModel.VerificationEmailAddress]: Period.Day,
    [ApiModel.VerificationGovernmentId]: Period.Day,
    [ApiModel.VerificationGovernmentIdNfc]: Period.Day,
    [ApiModel.VerificationMobileDriversLicense]: Period.Day,
    [ApiModel.VerificationPhoneNumber]: Period.Day,
    [ApiModel.VerificationSelfie]: Period.Day,
  },
  lastVerificationGranularities: {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    volume: undefined,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    checks: undefined,
  },
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  volumeLastQueryFilterRules: undefined,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  checksLastQueryFilterRules: undefined,
  selectedVolumeQueryTemplate: {
    template: undefined,
    computedOffsetStart: undefined,
    computedOffsetEnd: undefined,
  },
  selectedChecksQueryTemplate: {
    template: undefined,
    computedOffsetStart: undefined,
    computedOffsetEnd: undefined,
  },
};

// This is a function so we always get the most up-to-date created at times
const getInitialState = (): VerificationMetricsState => {
  const currentTime = DateTime.local();
  return {
    ...initialState,
    filters: {
      ...initialState.filters,
      createdAtStart: currentTime.minus({ days: 14 }).startOf('day').toISO(),
      createdAtEnd: currentTime.endOf('day').toISO(),
    },
    verificationVolumeOverTime: {
      ...initialState.verificationVolumeOverTime,
      domain: [
        currentTime.minus({ days: 14 }).startOf('day').toUnixInteger(),
        currentTime.endOf('day').toUnixInteger(),
      ],
    },
  };
};

export interface VerificationMetricsFilteredVolumeSummaryPayload {
  filteredVolumeSummary: VerificationMetricsFilteredVolumeResponse;
  queriedAt: Iso8601DateString;
  filterRules: Record<VerificationType, FilterRulesForType>;
}

interface VerificationMetricsVerificationChecksOverTimePayload {
  updatedAt: Iso8601DateString;
  verificationChecksOverTime: MetricsVerificationChecksOverTimeMetaData;
  queriedAt: Iso8601DateString;
}

export interface VerificationMetricsFilterParams {
  createdAtStart: Iso8601DateString;
  createdAtEnd: Iso8601DateString;
  templateIds: string[];
  txnTypeIds: string[];
  verificationTypes: string[];
  checkRequirements: CheckPassType[];
  checkTypes: CheckCategory[];
  groupBy?: MetricsGroupBy;
  groupByDimension?: Record<FilteredVerificationType, FilterDimensions>;
}

interface VerificationMetricsFilterRulesParams {
  filterRules: Record<VerificationType, FilterRulesForType>;
  volumeLastQueryFilterRules?: Record<VerificationType, FilterRulesForType>;
  checksLastQueryFilterRules?: Record<VerificationType, FilterRulesForType>;
}

interface SetVerificationVolumeOverTimePayload {
  verificationVolumeOverTime: VerificationVolumeIntervals;
  queriedAt: Iso8601DateString;
}

interface SetGranularityPayload {
  verificationGranularity: VerificationGranularitiesByType;
  verificationSurface: 'volume' | 'checks';
}

interface LoadQueryTemplatePayload {
  queryTemplate: AnalyticsQueryTemplate;
}

const { reducer, actions } = createSlice({
  name: 'metrics-verification',
  initialState,
  reducers: {
    setVerificationMetricsFilteredVolumeSummary: (
      state,
      action: PayloadAction<VerificationMetricsFilteredVolumeSummaryPayload>,
    ) => {
      const {
        filteredVolumeSummary: { filter_options: filterOptions, data },
      } = action.payload;
      return {
        ...state,
        filteredVolumeSummary: {
          data: data,
        },
        queriedAt: {
          ...state.queriedAt,
          filteredVolume: action.payload.queriedAt,
        },
        lastQueryFilters: {
          ...state.filters,
        },

        volumeLastQueryFilterRules: {
          ...state.filterRules,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ...(haveTopLevelFiltersChanged(state.lastQueryFilters, state.filters) &&
            initialState.filterRules),
        },
        filterOptions: {
          [FilterOptionKey.VerificationTemplates]: {
            ...filterOptions.verification_templates,
          },
          [FilterOptionKey.AamvaIssuingAuthorities]: [...filterOptions.aamva_issuing_authority],
          [FilterOptionKey.AamvaDeviceGroups]: [...filterOptions.aamva_device_groups],
          [FilterOptionKey.AamvaPlatforms]: [...filterOptions.aamva_platforms],
          [FilterOptionKey.GovernmentIdIssuingSubdivisions]: [
            ...filterOptions.government_id_issuing_subdivisions,
          ],
          [FilterOptionKey.GovernmentIdIssuingCountries]: [
            ...filterOptions.government_id_issuing_countries,
          ],
          [FilterOptionKey.GovernmentIdTypes]: [...filterOptions.government_id_types],
          [FilterOptionKey.GovernmentIdDeviceGroups]: [
            ...filterOptions.government_id_device_groups,
          ],
          [FilterOptionKey.GovernmentIdSelectedCountries]: [
            ...filterOptions.government_id_selected_countries,
          ],
          [FilterOptionKey.GovernmentIdCaptureMethods]: [
            ...filterOptions.government_id_capture_methods,
          ],
          [FilterOptionKey.DatabaseDeviceGroups]: [...filterOptions.database_device_groups],
          [FilterOptionKey.DatabaseCountryCodes]: [...filterOptions.database_country_codes],
          [FilterOptionKey.DatabasePlatforms]: [...filterOptions.database_platforms],
          [FilterOptionKey.SelfiePlatforms]: [...filterOptions.selfie_platforms],
          [FilterOptionKey.SelfieDeviceGroups]: [...filterOptions.selfie_device_groups],
          [FilterOptionKey.SelfieCaptureMethods]: [...filterOptions.selfie_capture_methods],
        },
      };
    },
    setMetricsVerificationChecksOverTime: (
      state,
      action: PayloadAction<VerificationMetricsVerificationChecksOverTimePayload>,
    ) => {
      const { filter_options: filterOptions, ...verificationChecksOverTime } =
        action.payload.verificationChecksOverTime;
      return {
        ...state,
        verificationChecksOverTime: verificationChecksOverTime,
        queriedAt: {
          ...state.queriedAt,
          checks: action.payload.queriedAt,
        },
        updatedAt: {
          ...state.updatedAt,
          checks: action.payload.updatedAt,
        },
        lastQueryFilters: {
          ...state.filters,
        },
        filterOptions: {
          [FilterOptionKey.VerificationTemplates]: {
            ...filterOptions.verification_templates,
          },
          [FilterOptionKey.AamvaIssuingAuthorities]: [...filterOptions.aamva_issuing_authority],
          [FilterOptionKey.AamvaDeviceGroups]: [...filterOptions.aamva_device_groups],
          [FilterOptionKey.AamvaPlatforms]: [...filterOptions.aamva_platforms],
          [FilterOptionKey.GovernmentIdIssuingSubdivisions]: [
            ...filterOptions.government_id_issuing_subdivisions,
          ],
          [FilterOptionKey.GovernmentIdIssuingCountries]: [
            ...filterOptions.government_id_issuing_countries,
          ],
          [FilterOptionKey.GovernmentIdTypes]: [...filterOptions.government_id_types],
          [FilterOptionKey.GovernmentIdDeviceGroups]: [
            ...filterOptions.government_id_device_groups,
          ],
          [FilterOptionKey.GovernmentIdSelectedCountries]: [
            ...filterOptions.government_id_selected_countries,
          ],
          [FilterOptionKey.GovernmentIdCaptureMethods]: [
            ...filterOptions.government_id_capture_methods,
          ],
          [FilterOptionKey.DatabaseDeviceGroups]: [...filterOptions.database_device_groups],
          [FilterOptionKey.DatabaseCountryCodes]: [...filterOptions.database_country_codes],
          [FilterOptionKey.DatabasePlatforms]: [...filterOptions.database_platforms],
          [FilterOptionKey.SelfiePlatforms]: [...filterOptions.selfie_platforms],
          [FilterOptionKey.SelfieDeviceGroups]: [...filterOptions.selfie_device_groups],
          [FilterOptionKey.SelfieCaptureMethods]: [...filterOptions.selfie_capture_methods],
        },
        checksLastQueryFilterRules: {
          ...state.filterRules,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ...(haveTopLevelFiltersChanged(state.lastQueryFilters, state.filters) &&
            initialState.filterRules),
        },
      };
    },
    clearMetrics: (state) => {
      return { ...getInitialState(), filters: state.filters };
    },
    resetFiltersToInitialState: (state, _payload: PayloadAction) => {
      state.filters = getInitialState().filters;
      state.filterRules = getInitialState().filterRules;
      state.selectedVolumeQueryTemplate = getInitialState().selectedVolumeQueryTemplate;
      state.selectedChecksQueryTemplate = getInitialState().selectedChecksQueryTemplate;
    },
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setMetricsFilters: (state, action: PayloadAction<Partial<VerificationMetricsFilterParams>>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ...(haveTopLevelFiltersChanged(state.lastQueryFilters, state.filters) && {
          groupByDimension: { ...getInitialState().filters.groupByDimension },
        }),
      };

      if (isEqual(newFilters, state.filters)) {
        // When the filters change but you're simply enacting a group by, allow that to propagate
        // since it's not going to fundamentally affect any of the template filters or dynamic values
        // This is very similar to what goes on in the granularity selector
        return {
          ...state,
          filters: {
            ...state.filters,
            groupByDimension: {
              ...state.filters.groupByDimension,
              ...action.payload.groupByDimension,
            },
          },
        };
      }

      return {
        ...state,
        filters: newFilters,
        // NOTE: Revising the below
        // when top level filters change, some dynamic filters may no longer be applicable.
        // We will need to address these within the components themselves through passing
        // down the data
        filterRules: {
          ...state.filterRules,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          ...(haveTopLevelFiltersChanged(state.filters, newFilters) && initialState.filterRules),
        },
      };
    },
    setVerificationFilterRules: (
      state,
      action: PayloadAction<VerificationMetricsFilterRulesParams>,
    ) => {
      const payload = {
        ...state,
        filterRules: {
          ...state.filterRules,
          ...action.payload.filterRules,
        } as Record<VerificationType, FilterRulesForType>,
        volumeLastQueryFilterRules: {
          ...state.volumeLastQueryFilterRules,
        },
        checksLastQueryFilterRules: {
          ...state.checksLastQueryFilterRules,
        },
      };
      if (action.payload.volumeLastQueryFilterRules != null) {
        payload.volumeLastQueryFilterRules = action.payload.volumeLastQueryFilterRules;
      }
      if (action.payload.checksLastQueryFilterRules != null) {
        payload.volumeLastQueryFilterRules = action.payload.checksLastQueryFilterRules;
      }
      return payload;
    },
    setGranularity: (state, action: PayloadAction<SetGranularityPayload>) => {
      state.verificationGranularities = {
        ...state.verificationGranularities,
        ...action.payload.verificationGranularity,
      };
      state.lastVerificationGranularities = {
        ...state.lastVerificationGranularities,
        [action.payload.verificationSurface]: action.payload.verificationGranularity,
      };
      state.lastQueryFilters = {
        ...state.filters,
      };
    },
    setVerificationVolumeOverTime: (
      state,
      action: PayloadAction<SetVerificationVolumeOverTimePayload>,
    ) => {
      return {
        ...state,
        verificationVolumeOverTime: action.payload.verificationVolumeOverTime,
        lastQueryFilters: {
          ...state.filters,
        },
        queriedAt: {
          ...state.queriedAt,
          filteredVolume: action.payload.queriedAt,
        },
      };
    },
    resetMetricsDateFilter: (state) => {
      return {
        ...getInitialState(),
        filters: {
          ...state.filters,
          createdAtStart: initialState.filters.createdAtStart,
          createdAtEnd: initialState.filters.createdAtEnd,
        },
      };
    },
    loadVolumeQueryTemplate: (state, action: PayloadAction<LoadQueryTemplatePayload>) => {
      const { query, 'relative-time-span': relativeTimeSpan } =
        action.payload.queryTemplate.attributes;

      const queryParams = query as VerificationVolumeAnalyticsQueryParams;
      const { createdAtStart, createdAtEnd } = computeOffsetForQueryTemplateLoad(
        relativeTimeSpan,
        DateTime.local().endOf('day'),
      );
      state.selectedVolumeQueryTemplate = {
        template: action.payload.queryTemplate,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        computedOffsetStart: createdAtStart.toISO(),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        computedOffsetEnd: createdAtEnd.toISO(),
      };
      state.filters = {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        createdAtStart: createdAtStart.toISO(),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        createdAtEnd: createdAtEnd.toISO(),
        templateIds: [...queryParams['template-ids'], ...queryParams['inquiry-template-ids']],
        verificationTypes: queryParams['verification-types'],
        checkRequirements: [],
        checkTypes: [],
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        groupBy: queryParams['group-by'],
        groupByDimension: getInitialState().filters.groupByDimension,
      };
      // for now, we only support top-level filters in query templates, so we clear any existing filterRules to make the query accurate
      state.filterRules = getInitialState().filterRules;
    },
    loadChecksQueryTemplate: (state, action: PayloadAction<LoadQueryTemplatePayload>) => {
      const { query, 'relative-time-span': relativeTimeSpan } =
        action.payload.queryTemplate.attributes;

      const queryParams = query as VerificationChecksAnalyticsQueryParams;
      const { createdAtStart, createdAtEnd } = computeOffsetForQueryTemplateLoad(
        relativeTimeSpan,
        DateTime.local().endOf('day'),
      );

      state.selectedChecksQueryTemplate = {
        template: action.payload.queryTemplate,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        computedOffsetStart: createdAtStart.toISO(),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        computedOffsetEnd: createdAtEnd.toISO(),
      };
      state.filters = {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        createdAtStart: createdAtStart.toISO(),
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        createdAtEnd: createdAtEnd.toISO(),
        templateIds: queryParams['template-ids'],
        verificationTypes: queryParams['verification-types'],
        checkRequirements: queryParams['check-requirements'] as CheckPassType[],
        checkTypes: queryParams['check-types'] as CheckCategory[],
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        groupBy: queryParams['group-by'],
        groupByDimension: getInitialState().filters.groupByDimension,
      };
      // for now, we only support top-level filters in query templates, so we clear any existing filterRules to make the query accurate
      state.filterRules = getInitialState().filterRules;
    },
    unsetSelectedVolumeQueryTemplate: (state) => {
      state.selectedVolumeQueryTemplate = getInitialState().selectedVolumeQueryTemplate;
    },
    unsetSelectedChecksQueryTemplate: (state) => {
      state.selectedChecksQueryTemplate = getInitialState().selectedChecksQueryTemplate;
    },
  },
});

const haveTopLevelFiltersChanged = (
  lastFilters: VerificationMetricsFilterParams,
  currentFilters: VerificationMetricsFilterParams,
) => {
  // handle initial state
  if (lastFilters == null) {
    return false;
  }

  return (
    !isEqual(lastFilters.createdAtStart, currentFilters.createdAtStart) ||
    !isEqual(lastFilters.createdAtEnd, currentFilters.createdAtEnd) ||
    !isEqual(lastFilters.checkRequirements, currentFilters.checkRequirements) ||
    !isEqual(lastFilters.templateIds, currentFilters.templateIds) ||
    !isEqual(lastFilters.verificationTypes, currentFilters.verificationTypes) ||
    !isEqual(lastFilters.groupBy, currentFilters.groupBy)
  );
};

export const {
  setVerificationMetricsFilteredVolumeSummary,
  setMetricsVerificationChecksOverTime,
  clearMetrics,
  setMetricsFilters,
  setVerificationFilterRules,
  setGranularity,
  setVerificationVolumeOverTime,
  resetMetricsDateFilter,
  resetFiltersToInitialState,
  loadVolumeQueryTemplate,
  loadChecksQueryTemplate,
  unsetSelectedVolumeQueryTemplate,
  unsetSelectedChecksQueryTemplate,
} = actions;

export const metricsVerificationReducer = reducer;
